import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import toast from 'react-hot-toast';

import { usersDataService } from 'services/users.service';

import { useUsersContext } from 'contexts/UsersContext';

import { SectionType } from 'sections/TabWrap';

import { EditForm } from './EditForm';

import { useDataForm } from './useDataForm.hooks';

const usePermissions = () => {
  const { user, userSet, form, onSubmit } = useUsersContext();

  const permissions = form?.watch('permissions');
  const isCatalogueValid = permissions?.includes('CATALOGUE');
  const isAdmin = permissions?.includes('ADMIN');

  const formData = useDataForm(isCatalogueValid, isAdmin, user);

  const { field } = useController({ control: form?.control, name: 'permissions' });

  const [values, valuesSet] = useState(field?.value || []);

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <EditForm
          isPermissions
          isDirty={form?.formState?.isDirty}
          onSubmit={onSubmit}
          data={formData}
          reset={form?.reset}
          control={form?.control}
          initialValues={user}
          onPermissionsChange={async (e, name) => {
            let valueCopy = [...values];

            if (e.target.checked) {
              valueCopy.push(name);
            } else {
              valueCopy = valueCopy.filter((el) => el !== name);
            }

            const updatedPermissions = valueCopy.filter(
              (valueItem: string) => valueItem
            );

            field.onChange(updatedPermissions);
            valuesSet(valueCopy);

            if (user?.id) {
              try {
                const res = await usersDataService.putUser(user.id, {
                  ...user,
                  permissions: updatedPermissions,
                });
                if (userSet) userSet(res.data);
                form?.reset(res.data);

                if (isAdmin && !updatedPermissions.includes('ADMIN')) {
                  toast(
                    'Admin revoked — season and zone assignments were cleared. Please reassign them in the "Seasons and lines" and "Zone" tabs.'
                  );
                }
              } catch {
                toast.error('Permission update failed!');
              }
            }
          }}
          watch={form?.watch}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!isCatalogueValid) {
      const permissionsFiltered = permissions?.filter(
        (item: string) =>
          item !== 'VIEW_PRICE' &&
          item !== 'VIEW_ORDER' &&
          item !== 'VIEW_INACTIVE_ITEMS' &&
          item !== 'DISTRIBUTOR' &&
          item !== 'VIEW_DISTRIBUTOR_PRICE' &&
          item !== 'INSERT_ORDER' &&
          item !== 'INSERT_BUDGET'
      );

      if (permissionsFiltered) {
        valuesSet(permissionsFiltered);
        form.setValue('permissions', permissionsFiltered);
      }
    }

    if (!isAdmin) {
      const permissionsFiltered = permissions?.filter(
        (item: string) => item !== 'NOTIFICATIONS' && item !== 'USERS'
      );

      if (permissionsFiltered) {
        valuesSet(permissionsFiltered);
        form.setValue('permissions', permissionsFiltered);
      }
    }
  }, [isCatalogueValid, isAdmin]);

  return {
    sections,
  };
};

export { usePermissions };
